// src/components/Navbar.jsx

import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  HiMenu,
  HiX,
  HiChevronDown,
  HiArrowRight,
  HiCheck,
} from "react-icons/hi";

import logo from "../assets/primeappconnect-logo-mark.png";

const services = [
  {
    number: "01",
    title: "Web Development",
    description: "Websites & powerful web applications",
    items: [
      "Business Websites",
      "E-Commerce Websites",
      "Booking Systems",
      "Custom Web Apps",
    ],
  },
  {
    number: "02",
    title: "Mobile App Development",
    description: "Modern apps for businesses & customers",
    items: [
      "Business Apps",
      "E-Commerce Apps",
      "Delivery Apps",
      "Custom Mobile Apps",
    ],
  },
  {
    number: "03",
    title: "Digital Marketing",
    description: "Grow visibility, traffic & qualified leads",
    items: [
      "Social Media",
      "Google Ads",
      "SEO",
      "Lead Generation",
    ],
  },
  {
    number: "04",
    title: "Video & AI Services",
    description: "Creative content powered by modern AI",
    items: [
      "Promotional Videos",
      "Reels & Shorts",
      "AI Marketing Videos",
      "AI Voiceover",
    ],
  },
  {
    number: "05",
    title: "CRM Development",
    description: "Smart systems for better business operations",
    items: [
      "Custom CRM",
      "Lead Management",
      "Sales Management",
      "Business Systems",
    ],
  },
];

const mainNavItems = [
  {
    to: "/",
    label: "Home",
  },
  {
    to: "/projects",
    label: "Projects",
  },
  {
    to: "/about",
    label: "About",
  },
  {
    to: "/contact",
    label: "Contact",
  },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();

  const isServicesActive =
    location.pathname === "/services" ||
    location.pathname.startsWith("/services/");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 font-[Manrope]">
      {/* =====================================================
          AMBIENT BACKGROUND
      ====================================================== */}

      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-blue-100/80 via-blue-50/40 to-transparent" />

      {/* =====================================================
          NAV CONTAINER
      ====================================================== */}

      <nav
        className={`relative mx-auto transition-all duration-300 ${
          scrolled
            ? "max-w-6xl px-3 pt-3"
            : "max-w-7xl px-4 pt-4 sm:px-6 lg:px-8"
        }`}
      >
        <div
          className={`relative flex h-[68px] items-center justify-between transition-all duration-300 ${
            scrolled
              ? "rounded-2xl border border-blue-100 bg-blue-50/95 px-4 shadow-[0_15px_45px_rgba(37,99,235,0.12)] backdrop-blur-xl sm:px-5"
              : "rounded-2xl border border-blue-100/80 bg-blue-50/80 px-3 shadow-[0_8px_30px_rgba(37,99,235,0.07)] backdrop-blur-lg sm:px-4"
          }`}
        >
          {/* Top highlight */}
          <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-blue-300/60 to-transparent" />

          {/* =================================================
              LOGO
          ================================================== */}

          <Link
            to="/"
            className="group flex shrink-0 items-center gap-2.5"
            aria-label="PrimeAppConnect Home"
          >
            <div className="relative flex h-10 w-10 items-center justify-center">
              {/* Glow */}
              <div className="absolute inset-0 rounded-xl bg-blue-500/20 blur-lg transition duration-500 group-hover:bg-blue-500/35" />

              {/* Logo box */}
              <div className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-blue-100 bg-white shadow-sm transition duration-300 group-hover:-translate-y-0.5 group-hover:border-blue-200 group-hover:shadow-md">
                <img
                  src={logo}
                  alt="PrimeAppConnect"
                  className="h-7 w-7 object-contain transition duration-300 group-hover:scale-105"
                />
              </div>
            </div>

            <div className="leading-none">
              <div className="text-[16px] font-extrabold tracking-tight sm:text-[17px]">
                <span className="text-slate-950">Prime</span>
                <span className="text-blue-600">App</span>
                <span className="text-cyan-500">Connect</span>
              </div>

              <div className="mt-1.5 text-[7px] font-extrabold tracking-[0.28em] text-blue-400/80 sm:text-[8px]">
                DIGITAL SOLUTIONS
              </div>
            </div>
          </Link>

          {/* =================================================
              DESKTOP NAVIGATION
          ================================================== */}

          <div className="hidden items-center md:flex">
            <div className="flex items-center gap-0.5">
              {/* Home */}
              <NavLink
                to="/"
                label="Home"
                active={location.pathname === "/"}
              />

              {/* =================================================
                  SERVICES DROPDOWN
              ================================================== */}

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setServicesOpen((prev) => !prev)}
                  className={`group relative flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold transition duration-200 ${
                    isServicesActive
                      ? "text-blue-600"
                      : "text-slate-600 hover:text-blue-600"
                  }`}
                  aria-expanded={servicesOpen}
                >
                  Services

                  <HiChevronDown
                    className={`text-sm transition duration-300 ${
                      servicesOpen
                        ? "rotate-180 text-blue-600"
                        : "text-slate-400 group-hover:text-blue-600"
                    }`}
                  />

                  <span
                    className={`absolute bottom-0 left-4 right-4 h-[2px] origin-center rounded-full bg-blue-600 transition duration-300 ${
                      isServicesActive
                        ? "scale-x-100 opacity-100"
                        : "scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100"
                    }`}
                  />
                </button>

                {/* Mega Menu */}
                <div
                  className={`absolute left-1/2 top-[57px] w-[720px] -translate-x-1/2 transition-all duration-200 ${
                    servicesOpen
                      ? "visible translate-y-0 opacity-100"
                      : "invisible -translate-y-2 opacity-0"
                  }`}
                >
                  <div className="overflow-hidden rounded-[22px] border border-blue-100 bg-white p-4 shadow-[0_25px_80px_rgba(37,99,235,0.16)]">
                    {/* Menu Header */}
                    <div className="mb-3 flex items-center justify-between border-b border-slate-100 px-2 pb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-blue-600" />

                          <p className="text-sm font-extrabold text-slate-950">
                            Our Services
                          </p>
                        </div>

                        <p className="mt-1 text-[10px] text-slate-400">
                          Digital solutions built around your business.
                        </p>
                      </div>

                      <Link
                        to="/services"
                        className="group inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-[10px] font-extrabold text-blue-600 transition duration-200 hover:bg-blue-100"
                      >
                        View all services

                        <HiArrowRight className="transition group-hover:translate-x-0.5" />
                      </Link>
                    </div>

                    {/* Services Grid */}
                    <div className="grid grid-cols-2 gap-1">
                      {services.map((service, index) => (
                        <Link
                          key={service.title}
                          to="/services"
                          className={`group rounded-2xl p-4 transition duration-200 hover:bg-blue-50/80 ${
                            index === services.length - 1
                              ? "col-span-2"
                              : ""
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Number */}
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[9px] font-extrabold text-blue-600 transition duration-300 group-hover:bg-white group-hover:shadow-sm">
                              {service.number}
                            </span>

                            {/* Details */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-xs font-extrabold text-slate-800 transition group-hover:text-blue-600">
                                  {service.title}
                                </p>

                                <HiArrowRight className="shrink-0 text-xs text-blue-200 opacity-0 transition duration-200 group-hover:translate-x-1 group-hover:text-blue-600 group-hover:opacity-100" />
                              </div>

                              <p className="mt-1 text-[10px] leading-4 text-slate-400">
                                {service.description}
                              </p>

                              <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1">
                                {service.items.slice(0, 3).map((item) => (
                                  <span
                                    key={item}
                                    className="flex items-center gap-1 text-[9px] font-medium text-slate-400"
                                  >
                                    <HiCheck className="text-blue-500" />
                                    {item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {/* Bottom strip */}
                    <div className="mt-2 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5">
                      <p className="text-[9px] font-semibold text-slate-400">
                        Need a custom digital solution?
                      </p>

                      <Link
                        to="/contact"
                        className="text-[9px] font-extrabold text-blue-600 transition hover:text-blue-700"
                      >
                        Let's talk →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* =================================================
                  PROJECTS / ABOUT / CONTACT
              ================================================== */}

              {mainNavItems.slice(1).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  active={location.pathname === item.to}
                />
              ))}
            </div>

            {/* =================================================
                DESKTOP CTA
            ================================================== */}

            <Link
              to="/contact"
              className="group ml-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-extrabold text-white shadow-lg shadow-blue-600/20 transition duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/25"
            >
              Get a Quote

              <HiArrowRight className="text-sm transition duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          {/* =================================================
              MOBILE MENU BUTTON
          ================================================== */}

          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-blue-100 bg-white text-slate-700 shadow-sm transition duration-200 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 md:hidden"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? (
              <HiX className="text-xl" />
            ) : (
              <HiMenu className="text-xl" />
            )}
          </button>
        </div>

        {/* =====================================================
            MOBILE MENU
        ====================================================== */}

        <div
          className={`overflow-hidden transition-all duration-300 md:hidden ${
            isOpen
              ? "max-h-[700px] pt-2 opacity-100"
              : "max-h-0 pt-0 opacity-0"
          }`}
        >
          <div className="rounded-2xl border border-blue-100 bg-blue-50/95 p-3 shadow-[0_18px_50px_rgba(37,99,235,0.14)] backdrop-blur-xl">
            <div className="space-y-1">
              {/* Home */}
              <MobileLink
                to="/"
                label="Home"
                active={location.pathname === "/"}
              />

              {/* Projects */}
              <MobileLink
                to="/projects"
                label="Projects"
                active={location.pathname === "/projects"}
              />

              {/* =================================================
                  MOBILE SERVICES
              ================================================== */}

              <div>
                <button
                  type="button"
                  onClick={() => setServicesOpen((prev) => !prev)}
                  className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold transition ${
                    isServicesActive
                      ? "bg-blue-100 text-blue-600"
                      : "text-slate-600 hover:bg-white/80 hover:text-blue-600"
                  }`}
                >
                  <span>Services</span>

                  <HiChevronDown
                    className={`transition-transform duration-300 ${
                      servicesOpen
                        ? "rotate-180 text-blue-600"
                        : "text-slate-400"
                    }`}
                  />
                </button>

                <div
                  className={`grid transition-all duration-300 ${
                    servicesOpen
                      ? "mt-1 grid-rows-[1fr]"
                      : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="ml-4 space-y-1 border-l border-blue-200 pl-3">
                      {services.map((service) => (
                        <Link
                          key={service.title}
                          to="/services"
                          className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-xs font-semibold text-slate-500 transition hover:bg-white hover:text-blue-600"
                        >
                          <span className="text-[9px] font-extrabold text-blue-400">
                            {service.number}
                          </span>

                          {service.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* About */}
              <MobileLink
                to="/about"
                label="About"
                active={location.pathname === "/about"}
              />

              {/* Contact */}
              <MobileLink
                to="/contact"
                label="Contact"
                active={location.pathname === "/contact"}
              />
            </div>

            {/* Mobile CTA */}
            <Link
              to="/contact"
              className="group mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-blue-600/20 transition duration-300 hover:bg-blue-700"
            >
              Get a Quote

              <HiArrowRight className="transition duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
}

/* ============================================================
   DESKTOP NAV LINK
============================================================ */

function NavLink({ to, label, active }) {
  return (
    <Link
      to={to}
      className={`group relative rounded-xl px-4 py-2.5 text-sm font-semibold transition duration-200 ${
        active
          ? "text-blue-600"
          : "text-slate-600 hover:text-blue-600"
      }`}
    >
      {label}

      <span
        className={`absolute bottom-0 left-4 right-4 h-[2px] origin-center rounded-full bg-blue-600 transition duration-300 ${
          active
            ? "scale-x-100 opacity-100"
            : "scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100"
        }`}
      />
    </Link>
  );
}

/* ============================================================
   MOBILE NAV LINK
============================================================ */

function MobileLink({ to, label, active }) {
  return (
    <Link
      to={to}
      className={`block rounded-xl px-4 py-3 text-sm font-semibold transition duration-200 ${
        active
          ? "bg-blue-100 text-blue-600"
          : "text-slate-600 hover:bg-white/80 hover:text-blue-600"
      }`}
    >
      {label}
    </Link>
  );
}